import { useState, useEffect } from 'react';
import { useAuth } from '../authentication/AuthContext';
import { useNavigate } from 'react-router-dom';
import '../styles/BetaAdminPage.css';

function BetaAdminPage() {
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [checkedApplicants, setCheckedApplicants] = useState({});
  const { user, loading: authLoading } = useAuth(); // Get both user and loading state
  const navigate = useNavigate();

  // Check if user is internal admin
  useEffect(() => {
    console.log('Checking authentication status...');
    console.log('user:', user);
    console.log('authLoading:', authLoading);
    
    // Only check authentication after authLoading is complete
    if (!authLoading) {
      if (user) {
        console.log('User is authenticated:', user.email);
        if (!user.email || !user.email.endsWith('@instantflows.com')) {
          console.log('User is not an internal admin, redirecting to home...');
          navigate('/');
        } else {
          // User is authenticated and has the right email, fetch applicants
          fetchApplicants();
        }
      } else {
        console.log('User is not authenticated, redirecting to login...');
        navigate('/login', { 
          state: { 
            from: '/admin/beta', 
            message: 'Please log in to access the admin area' 
          } 
        });
      }
    }
  }, [user, authLoading, navigate]);

  // Function to get the current user token
  const getCurrentUserToken = () => {
    // Try to get the token directly from localStorage first
    const idToken = localStorage.getItem('idToken');
    if (idToken) {
      console.log('Token found in localStorage');
      return idToken;
    }
    
    console.log('Trying to get token from Cognito session');
    
    // If not in localStorage, try to get it from the Cognito session
    if (user && user.signInUserSession && user.signInUserSession.idToken) {
      console.log('Token found in Cognito session');
      return user.signInUserSession.idToken.jwtToken;
    }
    
    // Try to get it using Amazon Cognito Identity SDK getCurrentSession
    if (user && typeof user.getSession === 'function') {
      try {
        // This is async but we're in a synchronous context, so we can't wait
        // Just return null for now, and the calling function will handle it
        user.getSession((err, session) => {
          if (err) {
            console.error('Error getting session:', err);
            return null;
          }
          if (session && session.idToken) {
            console.log('Token found via getSession');
            return session.idToken.jwtToken;
          }
        });
      } catch (error) {
        console.error('Error calling getSession:', error);
      }
    }
    
    // Last attempt - look for token in CognitoIdentityServiceProvider storage
    try {
      const lastAuthUser = localStorage.getItem(`CognitoIdentityServiceProvider.${process.env.REACT_APP_COGNITO_CLIENT_ID}.LastAuthUser`);
      if (lastAuthUser) {
        const userToken = localStorage.getItem(
          `CognitoIdentityServiceProvider.${process.env.REACT_APP_COGNITO_CLIENT_ID}.${lastAuthUser}.idToken`
        );
        if (userToken) {
          console.log('Token found in CognitoIdentityServiceProvider storage');
          return userToken;
        }
      }
    } catch (error) {
      console.error('Error accessing CognitoIdentityServiceProvider storage:', error);
    }
    
    console.error('No token found in any location');
    return null;
  };

  const fetchApplicants = async () => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      // Get token using our helper function
      const token = getCurrentUserToken();
      
      console.log('Token for API call:', token ? 'Token exists' : 'No token');
      
      // Make sure we have a token
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      const response = await fetch(`${API_URL}/admin/beta-applicants`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch applicants: ${response.status} ${errorText}`);
      }
      
      const data = await response.json();
      console.log('Applicants data:', data);
      setApplicants(data.applicants || []);
      
      // Initialize checked state
      const initialCheckedState = {};
      (data.applicants || []).forEach(app => {
        initialCheckedState[app.email] = app.isApproved;
      });
      setCheckedApplicants(initialCheckedState);
      
    } catch (err) {
      console.error('Error fetching applicants:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAccess = (email) => {
    setCheckedApplicants(prev => ({
      ...prev,
      [email]: !prev[email]
    }));
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const token = getCurrentUserToken();
      
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Create an array of promises for each applicant that needs updating
      const updatePromises = Object.entries(checkedApplicants).map(([email, grantAccess]) => {
        const applicant = applicants.find(a => a.email === email);
        // Only send requests for applicants whose status has changed
        if (applicant && applicant.isApproved !== grantAccess) {
          return fetch(`${API_URL}/admin/beta-applicants/update-access`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({ email, grantAccess })
          })
          .then(response => {
            if (!response.ok) {
              return response.text().then(text => {
                throw new Error(`Failed to update ${email}: ${response.status} ${text}`);
              });
            }
            return response.json();
          });
        }
        return Promise.resolve(); // No change needed
      });
      
      await Promise.all(updatePromises);
      
      // Refresh the list
      fetchApplicants();
      
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Failed to save changes: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Show loading when either auth is loading or we're fetching data
  if (authLoading || loading) return <div className="loading-spinner">Loading...</div>;
  if (error) return <div className="error-message">Error: {error}</div>;

  return (
    <div className="beta-admin-page">
      <div className="admin-container">
        <h1>Beta Applicants Management</h1>
        <p>Check/uncheck applicants to grant or revoke beta access.</p>
        
        <div className="applicants-list">
          <div className="list-header">
            <span>Email</span>
            <span>Application Date</span>
            <span>Beta Access</span>
          </div>
          
          {applicants.length === 0 ? (
            <div className="no-applicants">
              No beta applicants found.
            </div>
          ) : (
            applicants.map(applicant => (
              <div key={applicant.email} className="applicant-row">
                <span className="applicant-email">{applicant.email}</span>
                <span className="application-date">
                  {new Date(applicant.applicationDate).toLocaleDateString()}
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={checkedApplicants[applicant.email] || false}
                    onChange={() => handleToggleAccess(applicant.email)}
                  />
                  <span className="switch-slider"></span>
                </label>
              </div>
            ))
          )}
        </div>
        
        <div className="admin-actions">
          <button 
            className="save-button"
            onClick={handleSaveChanges}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BetaAdminPage;